import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useAuthentication from '../../../../customHooks/useAuthentication';
import useCaptcha from '../../../../customHooks/useCaptcha';
import { AppContext } from '../../../../utils/AppContext';
import { isLocalEnv } from '../../../../utils/env';
import { MixpanelContext, SCREENS } from '../../../../utils/mixpanel';
import Button from '../../../common/Button/Button';
import TextField from '../../../common/TextField/TextField';
import { RESET_USER_KEY } from '../../ResetPassword/ResetPassword';
import './PasswordForm.css';

interface Props {
  email: string;
  onLoginAttempt: () => void;
  handleLoginError: () => void;
  setErrorMessageKey: (error?: string) => void;
}

const PasswordForm: React.FC<Props> = ({
  email,
  onLoginAttempt,
  handleLoginError,
  setErrorMessageKey,
}) => {
  const history = useHistory();
  const [password, setPassword] = useState('');
  const [isButtonAnimating, setIsButtonAnimating] = useState(false);
  const {
    CaptchaComponent,
    renderCaptcha,
    resetCaptcha,
    getCaptchaResponse,
    showCaptchaError,
    isCaptchaRendered,
    isCaptchaError,
  } = useCaptcha();
  const { referrerURL } = useContext(AppContext);
  const { login } = useAuthentication();
  const { t } = useTranslation();
  const { sendMixpanelEvent, sendMixpanelError } = useContext(MixpanelContext);

  const onLoginClicked = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { response, shouldCheck } = getCaptchaResponse();
    if (password === '') {
      setErrorMessageKey('passwordEmpty');
      sendMixpanelError({
        email,
        errorKey: 'passwordEmpty',
        screen: SCREENS.PASSWORD_FORM,
      });
      return;
    }

    if (shouldCheck && !response) {
      setErrorMessageKey(undefined);
      showCaptchaError();
      return;
    }

    onLoginAttempt();
    setIsButtonAnimating(true);

    login(email, password, response)
      .then(({ token, requirePasswordReset }) => {
        if (requirePasswordReset) {
          window.localStorage.setItem(RESET_USER_KEY, JSON.stringify({ email, token }));
          history.push(`/reset${window.location.search}`);
          return;
        }

        referrerURL!.searchParams.append('authorization', token);
        if (isLocalEnv()) {
          window.location.assign(referrerURL!.href);
          return;
        }

        sendMixpanelEvent(
          'Logged in with password',
          { email, screen: SCREENS.PASSWORD_FORM },
          () => {
            window.location.assign(referrerURL!.href);
          }
        );
      })
      .catch((axiosError) => {
        // set timeout so the user will see the animation
        setTimeout(() => {
          const error = axiosError.response?.data?.error;
          handleLoginError();
          setIsButtonAnimating(false);
          const errorKey = `errors.${
            error?.type === 'unauthorized' ? 'short_description' : 'unknown'
          }`;
          setErrorMessageKey(errorKey);
          sendMixpanelError({
            email,
            errorKey,
            axiosError,
            screen: SCREENS.PASSWORD_FORM,
          });
          if (error?.parameters?.needCaptcha) {
            !isCaptchaRendered &&
              sendMixpanelEvent('Displayed captcha', {
                email,
                screen: SCREENS.PASSWORD_FORM,
              });
            renderCaptcha();
          }
          resetCaptcha();
        }, 750);
      });
  };

  const goToForgotPasswordScreen = () => {
    sendMixpanelEvent('Clicked on forgot password', {
      email,
      screen: SCREENS.PASSWORD_FORM,
    });
    history.push(`/forgot${window.location.search}`);
  };

  return (
    <form className="password-form" onSubmit={onLoginClicked}>
      <span className="title">{t('welcome')}</span>
      <span className="email">{email}</span>
      <TextField
        value={password}
        label={t('password')}
        placeholder={t('passwordPlaceholder')}
        type="password"
        id="password"
        onChange={(e) => setPassword(e.currentTarget.value)}
        actionComponent={
          <button
            type="button"
            className="forgot-password-button"
            onClick={goToForgotPasswordScreen}
          >
            {t('forgotPassword')}
          </button>
        }
      ></TextField>
      <CaptchaComponent showCaptchaError={isCaptchaError} />
      <Button text={t('login')} isAnimating={isButtonAnimating}></Button>
    </form>
  );
};

export default PasswordForm;
